"use client";

import type { SupportedLanguage } from "../../config/languages";
import classNames from "classnames/bind";
import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./PinnedStepsHero.module.css";

const cx = classNames.bind(styles);
const SCROLL_FRAMES_PER_STEP = 0.72;
const STEP_PROGRESS_GAIN = 1.12;

interface PinnedStepsHeroProps {
  locale?: SupportedLanguage;
  leadText?: string;
  steps?: string[];
  className?: string;
  ariaLabel?: string;
}

const PINNED_STEPS_HERO_CONTENT: Record<
  SupportedLanguage,
  {
    ariaLabel: string;
    leadText: string;
    steps: string[];
  }
> = {
  en: {
    ariaLabel: "Pinned steps hero",
    leadText: "Here, you will",
    steps: [
      "be trusted from day one.",
      "never sit on the sidelines.",
      "work on what the industry has not solved yet.",
      "leave a mark that outlasts your time.",
    ],
  },
  de: {
    ariaLabel: "Fixierter Schritte-Hero",
    leadText: "Here, you will",
    steps: [
      "be trusted from day one.",
      "never sit on the sidelines.",
      "work on what the industry has not solved yet.",
      "leave a mark that outlasts your time.",
    ],
  },
  // NOTE: Bad Claude German translations...
  // de: {
  //   ariaLabel: "Fixierter Schritte-Hero",
  //   leadText: "Hier wirst du",
  //   steps: [
  //     "ab Tag Eins Vertrauen bekommen.",
  //     "nicht am Rand stehen.",
  //     "an dem arbeiten, was die Branche noch nicht gelöst hat.",
  //     "Spuren hinterlassen, die deine Zeit überdauern.",
  //   ],
  // },
};

type PinnedStepsHeroStyle = CSSProperties & {
  "--pinned-steps-frames"?: string;
  "--step-offset"?: string;
};

export const PinnedStepsHero = ({
  locale = "en",
  leadText,
  steps,
  className,
  ariaLabel,
}: PinnedStepsHeroProps) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const stepsRef = useRef<HTMLUListElement | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [stepOffsets, setStepOffsets] = useState<number[]>([]);
  const localizedContent = PINNED_STEPS_HERO_CONTENT[locale];
  const resolvedLeadText = leadText ?? localizedContent.leadText;
  const resolvedSteps = steps ?? localizedContent.steps;
  const resolvedAriaLabel = ariaLabel ?? localizedContent.ariaLabel;

  const normalizedSteps = useMemo(
    () =>
      resolvedSteps
        .map((step) => step.trim())
        .filter((step) => step.length > 0),
    [resolvedSteps],
  );

  const stepCount = normalizedSteps.length;
  const scrollFrames = Math.max(2, stepCount * SCROLL_FRAMES_PER_STEP + 1);

  useEffect(() => {
    if (stepCount <= 1) {
      setActiveStep(0);
      return;
    }

    const section = sectionRef.current;
    if (!section) {
      return;
    }

    let frameId: number | null = null;

    const updateActiveStep = () => {
      frameId = null;
      const scrollableDistance = section.offsetHeight - window.innerHeight;
      if (scrollableDistance <= 0) {
        setActiveStep(0);
        return;
      }

      const sectionTop = section.getBoundingClientRect().top;
      const rawProgress = -sectionTop / scrollableDistance;
      const clampedProgress = Math.min(Math.max(rawProgress, 0), 1);
      const nextStep = Math.min(
        stepCount - 1,
        Math.floor(clampedProgress * stepCount * STEP_PROGRESS_GAIN),
      );

      setActiveStep((currentStep) =>
        currentStep === nextStep ? currentStep : nextStep,
      );
    };

    const queueUpdate = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(updateActiveStep);
    };

    queueUpdate();
    window.addEventListener("scroll", queueUpdate, { passive: true });
    window.addEventListener("resize", queueUpdate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      window.removeEventListener("scroll", queueUpdate);
      window.removeEventListener("resize", queueUpdate);
    };
  }, [stepCount]);

  useLayoutEffect(() => {
    const list = stepsRef.current;
    if (!list) {
      return;
    }

    const measureOffsets = () => {
      const items = Array.from(list.children) as HTMLElement[];
      let cumulativeOffset = 0;
      const nextOffsets = items.map((item) => {
        const offset = cumulativeOffset;
        cumulativeOffset += item.offsetHeight;
        return offset;
      });

      setStepOffsets((currentOffsets) => {
        if (
          currentOffsets.length === nextOffsets.length &&
          currentOffsets.every((offset, index) => offset === nextOffsets[index])
        ) {
          return currentOffsets;
        }

        return nextOffsets;
      });
    };

    measureOffsets();

    const observer = new ResizeObserver(measureOffsets);
    observer.observe(list);
    Array.from(list.children).forEach((child) => observer.observe(child));

    return () => {
      observer.disconnect();
    };
  }, [normalizedSteps]);

  if (stepCount === 0) {
    return null;
  }

  const rootStyle: PinnedStepsHeroStyle = {
    "--pinned-steps-frames": String(scrollFrames),
    "--step-offset": `${stepOffsets[activeStep] ?? 0}px`,
  };

  return (
    <section
      ref={sectionRef}
      className={cx("root", className)}
      style={rootStyle}
      aria-label={resolvedAriaLabel}
    >
      <div className={cx("sticky")}>
        <div className={cx("container")}>
          <div className={cx("content")}>
            <p className={cx("lead")}>{resolvedLeadText}</p>
            <ul ref={stepsRef} className={cx("steps")} aria-live="polite">
              {normalizedSteps.map((step, index) => (
                <li
                  key={`${step}-${index}`}
                  className={cx("step", {
                    active: activeStep === index,
                  })}
                >
                  {step}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
